import React, { useState } from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { ProofType } from "../../../types";
import { ProofDraft, defaultProofDraft } from "../../../context/MonyawnContext.types";
import { createProof, createCheckpoint, createTask } from "../../../workflow";

type Props = {
  onClose: () => void;
};

const proofTypeOptions: { value: ProofType; label: string }[] = [
  { value: "resume", label: "Resume" },
  { value: "job_description", label: "Job description" },
  { value: "message", label: "Message / email" },
  { value: "note", label: "Note" },
  { value: "offer", label: "Offer letter" },
  { value: "debrief", label: "Debrief" },
  { value: "other", label: "Other" },
];

export const AddProofModal: React.FC<Props> = ({ onClose }) => {
  const { selectedOpportunity, patchState } = useMonyawn();
  const [draft, setDraft] = useState<ProofDraft>(defaultProofDraft);
  const [error, setError] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedOpportunity) return;
    if (!draft.source_label.trim()) {
      setError("Give it a label so you can find it later.");
      return;
    }

    const proof = createProof({
      opportunity_id: selectedOpportunity.opportunity_id,
      ...draft,
    });
    const task = createTask(
      selectedOpportunity.opportunity_id,
      `Review ${draft.proof_type}: ${draft.source_label}`,
      "Play checker",
      true,
    );
    const checkpoint = createCheckpoint(
      selectedOpportunity,
      "Proof added",
      draft.source_label,
      "low",
      "proceed",
      `New ${draft.proof_type} added.`,
      "low",
      "low",
    );

    patchState(
      (current) => ({
        ...current,
        proof: [proof, ...current.proof],
        checkpoints: [checkpoint, ...current.checkpoints],
        tasks: [task, ...current.tasks],
      }),
      "Proof added.",
    );
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-5 animate-in slide-in-from-bottom-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-accent">
              Proof drop
            </p>
            <h2 className="text-xl font-bold text-brand-ink mt-1">Add to this play</h2>
          </div>
          <button
            onClick={onClose}
            className="text-brand-muted hover:text-brand-ink transition-colors mt-1"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                Type
              </span>
              <select
                className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none appearance-none"
                value={draft.proof_type}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, proof_type: e.target.value as ProofType }))
                }
              >
                {proofTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                Label
              </span>
              <input
                className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none"
                placeholder="e.g. Resume v2, JD from Indeed"
                value={draft.source_label}
                onChange={(e) => {
                  setError("");
                  setDraft((d) => ({ ...d, source_label: e.target.value }));
                }}
                required
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
              Why it matters
            </span>
            <input
              className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none"
              placeholder="One line about its role in this play"
              value={draft.evidence_note}
              onChange={(e) => setDraft((d) => ({ ...d, evidence_note: e.target.value }))}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
              Content / summary
            </span>
            <textarea
              className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
              rows={6}
              placeholder="Paste the full text or a clean summary"
              value={draft.content_summary}
              onChange={(e) => setDraft((d) => ({ ...d, content_summary: e.target.value }))}
            />
          </label>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              className="flex-1 bg-brand-accent text-white py-3 rounded-2xl font-bold hover:bg-brand-accent-strong transition-all shadow-sm active:scale-95"
            >
              Add proof
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-brand-surface border border-black/10 text-brand-ink px-6 py-3 rounded-2xl font-medium hover:bg-black/5 transition-all active:scale-95"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
